export const COMMUNITY_PERMISSIONS = [
  'create_subcommunity',
  'create_general_post',
  'manage_new_members',
  'manage_permissions',
  'view_active_user_status',
] as const;

export type CommunityPermission = (typeof COMMUNITY_PERMISSIONS)[number];

export const CONFIGURABLE_COMMUNITY_PERMISSIONS = [
  'create_subcommunity',
  'create_general_post',
  'manage_new_members',
  'view_active_user_status',
] as const;

export type ConfigurableCommunityPermission = (typeof CONFIGURABLE_COMMUNITY_PERMISSIONS)[number];

export const MATRIX_COMMUNITY_ROLES = ['MEMBER', 'LEADERSHIP', 'COMMITTEE'] as const;
export type MatrixCommunityRole = (typeof MATRIX_COMMUNITY_ROLES)[number];

export const COMMUNITY_PERMISSION_LABELS: Record<ConfigurableCommunityPermission, string> = {
  create_subcommunity: 'Create sub-community',
  create_general_post: 'Create general post',
  manage_new_members: 'Manage new members',
  view_active_user_status: 'View active user status',
};

export const MATRIX_COMMUNITY_ROLE_LABELS: Record<MatrixCommunityRole, string> = {
  MEMBER: 'Member',
  LEADERSHIP: 'Leadership',
  COMMITTEE: 'Committee',
};

export function hasCommunityPermission(
  permissions: readonly string[] | undefined,
  permission: CommunityPermission
): boolean {
  return permissions?.includes(permission) ?? false;
}

export function communitiesUserCanCreateUnder<
  T extends { permissions?: readonly string[] | undefined },
>(communities: T[]): T[] {
  return communities.filter((community) =>
    hasCommunityPermission(community.permissions, 'create_subcommunity')
  );
}

export function canCreateTopLevelCommunity(options: {
  isWhitelabel: boolean;
  allowCreateCommunity?: boolean;
}): boolean {
  if (options.isWhitelabel) return false;
  return options.allowCreateCommunity !== false;
}

export function canCreateCommunity(options: {
  isWhitelabel: boolean;
  allowCreateCommunity?: boolean;
  communities: { permissions?: readonly string[] | undefined }[];
}): boolean {
  return (
    canCreateTopLevelCommunity(options) ||
    communitiesUserCanCreateUnder(options.communities).length > 0
  );
}

export function canJoinMoreCommunities(options: {
  isWhitelabel: boolean;
  catalogCount?: number;
  joinedCount: number;
}): boolean {
  if (!options.isWhitelabel) return true;
  const catalogCount = options.catalogCount ?? 0;
  return catalogCount === 0 || options.joinedCount < catalogCount;
}
